import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { useGetUserDetailsQuery, useUpdateUserStatusMutation } from '../store/slices/apiSlice';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { 
    Mail, 
    Smartphone, 
    DollarSign, 
    TrendingUp, 
    CheckCircle, 
    XCircle, 
    ChevronLeft, 
    Calendar, 
    Shield, 
    User as UserIcon,
    ArrowUpRight,
    Activity,
    CreditCard,
    FileCheck
} from 'lucide-react';
import UserAvatar from '../components/common/UserAvatar';
import toast from 'react-hot-toast';

const UserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const { data, isLoading, error, refetch } = useGetUserDetailsQuery({ user_id: Number(id) });
    const [updateUserStatus] = useUpdateUserStatusMutation();
    const user = data?.data;

    const formatCurrency = (amount: string | number | null | undefined, currencySymbol: string = '$') => {
        const value = parseFloat(String(amount || '0')) || 0;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value).replace('$', currencySymbol);
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const handleStatusToggle = async () => {
        if (!user) return;
        try {
            await updateUserStatus({
                user_id: user.user_id,
                is_active: !user.is_active
            }).unwrap();
            toast.success(`User ${!user.is_active ? 'activated' : 'deactivated'} successfully`);
            refetch();
        } catch (err) {
            toast.error('Failed to update user status');
        }
    };

    const investmentTableColumns = (currency_symbol: string) => [
        {
            key: 'sno',
            label: 'S.No',
            render: (_: any, __: any, index: number) => (
                <span className="font-bold text-gray-400 text-xs">#{index}</span>
            ),
        },
        {
            key: 'project',
            label: 'PROJECT',
            render: (_: any, row: any) => (
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 font-bold">
                        {row.project?.title?.charAt(0) || 'P'}
                    </div>
                    <span className="font-bold text-gray-900">{row.project?.title || 'Unknown Project'}</span>
                </div>
            ),
        },
        {
            key: 'created_at',
            label: 'INVESTMENT DATE',
            render: (v: string) => (
                <span className="text-gray-600 font-medium">{formatDateUtil(v)}</span>
            ),
        },
        {
            key: 'amount_invested',
            label: 'AMOUNT',
            render: (v: any) => (
                <span className="font-bold text-gray-900">{formatCurrency(v, currency_symbol)}</span>
            ),
        },
        {
            key: 'status',
            label: 'STATUS',
            render: (v: string) => (
                <Badge 
                    variant={v === 'approved' ? 'success' : v === 'pending' ? 'warning' : 'danger'}
                    className="font-bold uppercase text-[10px] tracking-wider"
                >
                    {v}
                </Badge>
            ),
        },
        {
            key: 'total_roi_received',
            label: 'RETURNS RECEIVED',
            render: (v: any) => (
                <span className="text-emerald-600 font-bold">+{formatCurrency(v, currency_symbol)}</span>
            ),
        },
    ];

    const filteredInvestments = useMemo(() => {
        if (!user?.investments) return [];
        if (!searchTerm) return user.investments;
        const lower = searchTerm.toLowerCase();
        return user.investments.filter((inv: any) => {
            const projectTitle = inv.project?.title?.toLowerCase() || '';
            const status = inv.status?.toLowerCase() || '';
            return projectTitle.includes(lower) || status.includes(lower);
        });
    }, [user?.investments, searchTerm]);

    if (isLoading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
    
    if (error || !user) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center p-8 bg-white rounded-2xl shadow-xl">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900">User Not Found</h2>
                <p className="text-gray-500 mb-6">The user you are looking for does not exist or has been removed.</p>
                <Button onClick={() => navigate('/admin/users')}>Back to Users</Button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50/50 pb-20">
            {/* Professional Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-30 px-4 py-4 sm:px-8">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center space-x-4">
                        <button 
                            onClick={() => navigate('/admin/users')}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">{user.name}</h1>
                                {user.is_verified ? (
                                    <Badge variant="success" className="bg-emerald-100 text-emerald-700 border-none font-bold text-[10px]">VERIFIED</Badge>
                                ) : (
                                    <Badge variant="warning" className="bg-amber-100 text-amber-700 border-none font-bold text-[10px]">PENDING</Badge>
                                )}
                            </div>
                            <p className="text-sm text-gray-500 font-medium flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" /> {user.email}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <Button
                            variant="outline"
                            onClick={handleStatusToggle}
                            className={`flex-1 sm:flex-none font-bold text-xs ${user.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}
                        >
                            {user.is_active ? 'Deactivate Account' : 'Activate Account'}
                        </Button>
                        <Button className="flex-1 sm:flex-none font-bold text-xs shadow-lg shadow-blue-500/20">
                            Download Report
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-8 mt-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {[
                        { label: 'Wallet Balance', value: user.wallet_balance, icon: CreditCard, color: 'text-blue-600', bg: 'bg-blue-50' },
                        { label: 'Total Invested', value: user.total_invested, icon: DollarSign, color: 'text-purple-600', bg: 'bg-purple-50' },
                        { label: 'Expected ROI', value: user.total_expected_roi, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50' },
                        { label: 'Received ROI', value: user.total_received_roi, icon: Activity, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    ].map((stat, i) => (
                        <Card key={i} className="border-none shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{stat.label}</p>
                                    <p className="text-2xl font-black text-gray-900">{formatCurrency(stat.value, user.currency_symbol)}</p>
                                </div>
                                <div className={`p-3 rounded-2xl ${stat.bg} ${stat.color}`}>
                                    <stat.icon className="w-6 h-6" />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Profile & Info */}
                    <div className="space-y-8">
                        <Card className="overflow-hidden border-none shadow-sm">
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 flex flex-col items-center text-center">
                                <div className="relative mb-4">
                                    <UserAvatar
                                        src={user.profile_image}
                                        name={user.name}
                                        size="xl"
                                        className="w-24 h-24 ring-4 ring-white/20 shadow-2xl"
                                    />
                                    <div className="absolute bottom-1 right-1 w-6 h-6 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center">
                                        <CheckCircle className="w-4 h-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-1">{user.name}</h3>
                                <p className="text-blue-100 text-sm font-medium opacity-80">User ID: #{user.user_id}</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 font-medium">Account Status</span>
                                    <Badge variant={user.is_active ? 'success' : 'danger'} className="font-bold">
                                        {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500 font-medium">Profile Completeness</span>
                                    <span className={`font-bold ${user.is_complete_profile ? 'text-emerald-600' : 'text-amber-600'}`}>
                                        {user.is_complete_profile ? '100% (Complete)' : 'Incomplete'}
                                    </span>
                                </div>
                                {user.serial_number && (
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 font-medium">Serial Number</span>
                                        <span className="text-blue-600 font-mono font-extrabold bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                                            #{user.serial_number}
                                        </span>
                                    </div>
                                )}
                                <div className="pt-4 border-t border-gray-100">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Personal Details</p>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
                                            <div>
                                                <p className="text-xs text-gray-400 font-medium">Email Address</p>
                                                <p className="text-sm text-gray-800 font-bold">{user.email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Smartphone className="w-4 h-4 text-gray-400 mt-0.5" />
                                            <div>
                                                <p className="text-xs text-gray-400 font-medium">Phone Number</p>
                                                <p className="text-sm text-gray-800 font-bold">{user.phone || user.mobile_number || 'Not Provided'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                                            <div>
                                                <p className="text-xs text-gray-400 font-medium">Member Since</p>
                                                <p className="text-sm text-gray-800 font-bold">{formatDate(user.created_at)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <Card className="border-none shadow-sm p-6">
                            <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Shield className="w-4 h-4 text-blue-600" />
                                Security & Verification
                            </h4>
                            <div className="space-y-4">
                                <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                                            <FileCheck className="w-4 h-4 text-emerald-600" />
                                        </div>
                                        <span className="text-xs font-bold text-gray-700">KYC Status</span>
                                    </div>
                                    <Badge variant={user.is_verified ? 'success' : 'warning'} className="font-black text-[10px]">
                                        {user.is_verified ? 'VERIFIED' : 'PENDING'}
                                    </Badge>
                                </div>
                                <div className="p-3 bg-gray-50 rounded-xl flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                                            <Smartphone className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <span className="text-xs font-bold text-gray-700">2FA Enabled</span>
                                    </div>
                                    <span className="text-xs font-black text-gray-400 uppercase">Disabled</span>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Right Column: Investments & History */}
                    <div className="lg:col-span-2 space-y-8">
                        <Card className="border-none shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-blue-600" />
                                    Investment Portfolio
                                </h3>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search investments..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className="pl-4 pr-4 py-1.5 text-xs border border-gray-200 rounded-full w-48 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                    />
                                </div>
                            </div>
                            <div className="p-0">
                                {user.investments && user.investments.length > 0 ? (
                                    <Table
                                        columns={investmentTableColumns(user.currency_symbol)}
                                        data={filteredInvestments}
                                        searchable={false}
                                        itemsPerPage={10}
                                    />
                                ) : (
                                    <div className="py-20 text-center">
                                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <TrendingUp className="w-8 h-8 text-gray-300" />
                                        </div>
                                        <h4 className="text-gray-900 font-bold">No Investments Yet</h4>
                                        <p className="text-gray-500 text-sm">This user hasn't made any investments on the platform.</p>
                                    </div>
                                )}
                            </div>
                        </Card>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;